import { useEffect, useRef, useState } from 'react'
import { WingMark } from '@/components/public/motifs'

/**
 * The hero's rotating word: wipes out/in behind an amber underline while a
 * wing glyph crosses it (CSS in index.css, `.rotw`). SSR renders the first
 * word arrives after the first bird pass, rather than rendering and shifting
 * the period during hydration. Screen readers get a single static word – the
 * animated copy is aria-hidden.
 */
export default function RotatingWord({ words, interval = 3800 }) {
  // Intentionally empty for the server render and first client paint. It gives
  // the lead bird a clean entrance and avoids a transient, wrongly-positioned
  // period while fonts and the word holder settle.
  const [word, setWord] = useState('')
  const [anim, setAnim] = useState('')
  const holderRef = useRef(null)
  const rulerRef = useRef(null)
  const wingRef = useRef(null)

  useEffect(() => {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    let i = 0
    let swapTimer = 0
    let introTimer = 0

    if (reduce) {
      setWord(words[0])
      // Still rotate the copy, just with no wipe/wing/width animation.
      const id = setInterval(() => {
        i = (i + 1) % words.length
        setWord(words[i])
      }, interval)
      return () => clearInterval(id)
    }

    const holder = holderRef.current
    const ruler = rulerRef.current
    const widthOf = (w) => {
      ruler.textContent = w ? `${w}.` : ''
      return ruler.getBoundingClientRect().width
    }
    // Only pin the holder's width once the real webfont is in: measuring
    // against the fallback font freezes a width the swapped-in Merriweather
    // overflows, shoving the word into the period after it. Until then the
    // holder stays auto-width, which is always correct for a static word.
    let cancelled = false
    const pinWidth = () => {
      if (!cancelled) holder.style.width = `${widthOf(words[i])}px`
    }
    if (document.fonts?.status === 'loaded') pinWidth()
    else if (document.fonts?.ready) document.fonts.ready.then(pinWidth)
    else pinWidth()

    const flyWing = () => {
      const wing = wingRef.current
      if (!wing) return
      wing.classList.remove('rotw-fly')
      requestAnimationFrame(() => requestAnimationFrame(() => wing.classList.add('rotw-fly')))
    }
    const enter = (next) => {
      holder.style.width = `${widthOf(next)}px`
      flyWing()
      setWord(next)
      setAnim('rotw-in')
    }

    // The headline begins blank, then the first bird sweeps the first word in.
    // Width is set immediately before that pass, so the period never occupies
    // an in-between position once the animated word is visible.
    introTimer = setTimeout(() => enter(words[0]), 180)

    const id = setInterval(() => {
      i = (i + 1) % words.length
      const next = words[i]
      setAnim('rotw-out')
      holder.style.width = `${widthOf(next)}px`
      flyWing()
      // Swap the text once the wipe-out (0.3s) is mostly done.
      swapTimer = setTimeout(() => {
        setWord(next)
        setAnim('rotw-in')
      }, 270)
    }, interval)

    return () => {
      cancelled = true
      clearInterval(id)
      clearTimeout(introTimer)
      clearTimeout(swapTimer)
    }
  }, [words, interval])

  return (
    <>
      <span className="sr-only">{words[0]}.</span>
      <span ref={holderRef} className="rotw" aria-hidden="true">
        <span className={`rotw-w${anim ? ` ${anim}` : ''}`}>{word ? `${word}.` : ''}</span>
        <span
          ref={rulerRef}
          className="rotw-w"
          style={{ position: 'absolute', left: 0, top: 0, visibility: 'hidden', whiteSpace: 'nowrap' }}
        />
        <WingMark ref={wingRef} className="rotw-wing" />
      </span>
    </>
  )
}
